// src/utils/sharingDb.js
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot,
  deleteDoc,
  increment,
  setDoc,
  arrayUnion
} from "firebase/firestore";
import { db } from "../firebase";
import { auth } from "../firebase";

// Collections
const SHARED_PATTERNS_COLLECTION = "sharedPatterns";
const SHARED_PRACTICE_PLANS_COLLECTION = "sharedPracticePlans";
const SHARED_PRACTICE_SESSIONS_COLLECTION = "sharedPracticeSessions";
const SHARED_RECORDINGS_COLLECTION = "sharedRecordings";
const PUBLIC_SHARED_RECORDINGS_COLLECTION = "publicSharedRecordings";
const SHARE_COMMENTS_COLLECTION = "shareComments";

// ===== Pattern Sharing Functions =====

/**
 * Share a pattern (.ff file) with a connected user
 * @param {Object} patternData - The pattern data to share
 * @param {string} fromUserId - ID of the user sharing the pattern
 * @param {string} toUserId - ID of the user receiving the pattern
 * @param {string} message - Optional message to include with the shared pattern
 * @returns {Promise} - Promise that resolves with the new document reference
 */
export async function sharePattern(patternData, fromUserId, toUserId, message = "") {
  const sharedPatternsRef = collection(db, SHARED_PATTERNS_COLLECTION);
  
  return addDoc(sharedPatternsRef, {
    patternData,
    fromUserId,
    toUserId,
    message,
    sharedAt: serverTimestamp(),
    viewed: false,
    status: "shared" // Can be "shared", "accepted", "declined", "archived"
  });
}

/**
 * Get patterns shared with a user
 * @param {string} userId - The user ID to get shared patterns for
 * @param {function} callback - Callback function that receives the snapshot
 * @returns {function} - Unsubscribe function
 */
export function getSharedPatternsWithUser(userId, callback) {
  const sharedPatternsRef = collection(db, SHARED_PATTERNS_COLLECTION);
  const q = query(
    sharedPatternsRef,
    where("toUserId", "==", userId),
    orderBy("sharedAt", "desc")
  );
  
  return onSnapshot(q, callback);
}

/**
 * Get patterns shared by a user
 * @param {string} userId - The user ID who shared the patterns
 * @param {function} callback - Callback function that receives the snapshot
 * @returns {function} - Unsubscribe function
 */
export function getSharedPatternsByUser(userId, callback) {
  const sharedPatternsRef = collection(db, SHARED_PATTERNS_COLLECTION);
  const q = query(
    sharedPatternsRef,
    where("fromUserId", "==", userId),
    orderBy("sharedAt", "desc")
  );
  
  return onSnapshot(q, callback);
}

/**
 * Update a shared pattern's status
 * @param {string} patternId - The shared pattern document ID
 * @param {string} newStatus - The new status ("accepted", "declined", "archived")
 * @returns {Promise} - Promise that resolves when the update is complete
 */
export async function updateSharedPatternStatus(patternId, newStatus) {
  const patternRef = doc(db, SHARED_PATTERNS_COLLECTION, patternId);
  return updateDoc(patternRef, {
    status: newStatus,
    updatedAt: serverTimestamp()
  });
}

/**
 * Mark a shared pattern as viewed
 * @param {string} patternId - The shared pattern document ID
 * @returns {Promise} - Promise that resolves when the update is complete
 */
export async function markPatternAsViewed(patternId) {
  const patternRef = doc(db, SHARED_PATTERNS_COLLECTION, patternId);
  return updateDoc(patternRef, {
    viewed: true,
    viewedAt: serverTimestamp()
  });
}

// ===== Practice Plan Sharing Functions =====

/**
 * Share a practice plan with a connected user
 * @param {Object} planData - The practice plan data to share
 * @param {string} fromUserId - ID of the user sharing the plan
 * @param {string} toUserId - ID of the user receiving the plan
 * @param {string} message - Optional message to include with the shared plan
 * @returns {Promise} - Promise that resolves with the new document reference
 */
export async function sharePracticePlan(planData, fromUserId, toUserId, message = "") {
  const sharedPlansRef = collection(db, SHARED_PRACTICE_PLANS_COLLECTION);
  
  return addDoc(sharedPlansRef, {
    planData,
    fromUserId,
    toUserId,
    message,
    sharedAt: serverTimestamp(),
    viewed: false,
    status: "shared" // Can be "shared", "accepted", "declined", "archived"
  });
}

/**
 * Get practice plans shared with a user
 * @param {string} userId - The user ID to get shared plans for
 * @param {function} callback - Callback function that receives the snapshot
 * @returns {function} - Unsubscribe function
 */
export function getSharedPracticePlansWithUser(userId, callback) {
  const sharedPlansRef = collection(db, SHARED_PRACTICE_PLANS_COLLECTION);
  const q = query(
    sharedPlansRef,
    where("toUserId", "==", userId),
    orderBy("sharedAt", "desc")
  );
  
  return onSnapshot(q, callback);
}

/**
 * Get practice plans shared by a user
 * @param {string} userId - The user ID who shared the plans
 * @param {function} callback - Callback function that receives the snapshot
 * @returns {function} - Unsubscribe function
 */
export function getSharedPracticePlansByUser(userId, callback) {
  const sharedPlansRef = collection(db, SHARED_PRACTICE_PLANS_COLLECTION);
  const q = query(
    sharedPlansRef,
    where("fromUserId", "==", userId),
    orderBy("sharedAt", "desc")
  );
  
  return onSnapshot(q, callback);
}

/**
 * Update a shared practice plan's status
 * @param {string} planId - The shared plan document ID
 * @param {string} newStatus - The new status ("accepted", "declined", "archived")
 * @returns {Promise} - Promise that resolves when the update is complete
 */
export async function updateSharedPracticePlanStatus(planId, newStatus) {
  const planRef = doc(db, SHARED_PRACTICE_PLANS_COLLECTION, planId);
  return updateDoc(planRef, {
    status: newStatus,
    updatedAt: serverTimestamp()
  });
}

/**
 * Mark a shared practice plan as viewed
 * @param {string} planId - The shared plan document ID
 * @returns {Promise} - Promise that resolves when the update is complete
 */
export async function markPracticePlanAsViewed(planId) {
  const planRef = doc(db, SHARED_PRACTICE_PLANS_COLLECTION, planId);
  return updateDoc(planRef, {
    viewed: true,
    viewedAt: serverTimestamp()
  });
}

// ===== Practice Session Sharing Functions =====

/**
 * Share a practice session with a connected user
 * @param {Object} sessionData - The practice session data to share
 * @param {string} fromUserId - ID of the user sharing the session
 * @param {string} toUserId - ID of the user receiving the session
 * @param {string} message - Optional message to include with the shared session
 * @param {Object} patternData - Optional pattern data to include with the shared session
 * @returns {Promise} - Promise that resolves with the new document reference
 */
export async function sharePracticeSession(sessionData, fromUserId, toUserId, message = "", patternData = null) {
  const sharedSessionsRef = collection(db, SHARED_PRACTICE_SESSIONS_COLLECTION);
  
  return addDoc(sharedSessionsRef, {
    sessionData,
    fromUserId,
    toUserId,
    message,
    patternData, // Include pattern data if provided
    sharedAt: serverTimestamp(),
    viewed: false,
    status: "shared", // Can be "shared", "reviewed", "archived"
    reviewNotes: ""
  });
}

/**
 * Get practice sessions shared with a user
 * @param {string} userId - The user ID to get shared sessions for
 * @param {function} callback - Callback function that receives the snapshot
 * @returns {function} - Unsubscribe function
 */
export function getSharedPracticeSessionsWithUser(userId, callback) {
  const sharedSessionsRef = collection(db, SHARED_PRACTICE_SESSIONS_COLLECTION);
  const q = query(
    sharedSessionsRef,
    where("toUserId", "==", userId),
    orderBy("sharedAt", "desc")
  );
  
  return onSnapshot(q, callback);
}

/**
 * Get practice sessions shared by a user
 * @param {string} userId - The user ID who shared the sessions
 * @param {function} callback - Callback function that receives the snapshot
 * @returns {function} - Unsubscribe function
 */
export function getSharedPracticeSessionsByUser(userId, callback) {
  const sharedSessionsRef = collection(db, SHARED_PRACTICE_SESSIONS_COLLECTION);
  const q = query(
    sharedSessionsRef,
    where("fromUserId", "==", userId),
    orderBy("sharedAt", "desc")
  );
  
  return onSnapshot(q, callback);
}

/**
 * Add review notes to a shared practice session
 * @param {string} sessionId - The shared session document ID
 * @param {string} reviewNotes - The review notes to add
 * @returns {Promise} - Promise that resolves when the update is complete
 */
export async function addPracticeSessionReview(sessionId, reviewNotes) {
  const sessionRef = doc(db, SHARED_PRACTICE_SESSIONS_COLLECTION, sessionId);
  return updateDoc(sessionRef, {
    reviewNotes,
    status: "reviewed",
    reviewedAt: serverTimestamp()
  });
}

/**
 * Mark a shared practice session as viewed
 * @param {string} sessionId - The shared session document ID
 * @returns {Promise} - Promise that resolves when the update is complete
 */
export async function markPracticeSessionAsViewed(sessionId) {
  const sessionRef = doc(db, SHARED_PRACTICE_SESSIONS_COLLECTION, sessionId);
  return updateDoc(sessionRef, {
    viewed: true,
    viewedAt: serverTimestamp()
  });
}

/**
 * Update a shared practice session's status
 * @param {string} sessionId - The shared session document ID
 * @param {string} newStatus - The new status ("shared", "reviewed", "archived")
 * @returns {Promise} - Promise that resolves when the update is complete
 */
export async function updateSharedPracticeSessionStatus(sessionId, newStatus) {
  const sessionRef = doc(db, SHARED_PRACTICE_SESSIONS_COLLECTION, sessionId);
  return updateDoc(sessionRef, {
    status: newStatus,
    updatedAt: serverTimestamp()
  });
}

// ===== Comments and Discussion Functions =====

/**
 * Add a comment to a shared item
 * @param {string} sharedItemId - The ID of the shared item (pattern/plan/session)
 * @param {string} collectionName - The collection name of the shared item
 * @param {string} senderId - The user ID of the comment sender
 * @param {string} content - The comment content
 * @returns {Promise} - Promise that resolves with the new document reference
 */
export async function addShareComment(sharedItemId, collectionName, senderId, content) {
  const commentsRef = collection(db, SHARE_COMMENTS_COLLECTION);
  
  return addDoc(commentsRef, {
    sharedItemId,
    collectionName,
    senderId,
    content,
    timestamp: serverTimestamp(),
    isRead: false
  });
}

/**
 * Get comments for a shared item
 * @param {string} sharedItemId - The ID of the shared item
 * @param {function} callback - Callback function that receives the snapshot
 * @returns {function} - Unsubscribe function
 */
export function getShareComments(sharedItemId, callback) {
  const commentsRef = collection(db, SHARE_COMMENTS_COLLECTION);
  const q = query(
    commentsRef,
    where("sharedItemId", "==", sharedItemId),
    orderBy("timestamp", "asc")
  );
  
  return onSnapshot(q, callback);
}

/**
 * Mark a comment as read
 * @param {string} commentId - The comment document ID
 * @returns {Promise} - Promise that resolves when the update is complete
 */
export async function markCommentAsRead(commentId) {
  const commentRef = doc(db, SHARE_COMMENTS_COLLECTION, commentId);
  return updateDoc(commentRef, {
    isRead: true
  });
}

/**
 * Get unread comment count for a user
 * @param {string} userId - The user ID to get unread count for
 * @param {function} callback - Callback function that receives the count
 * @returns {function} - Unsubscribe function
 */
export async function getUnreadCommentCount(userId, callback) {
  // First get all shared items where user is either sender or receiver
  const [patternsWith, patternsBy, plansWith, plansBy, sessionsWith, sessionsBy, recordingsWith, recordingsBy] = await Promise.all([
    getDocs(query(collection(db, SHARED_PATTERNS_COLLECTION), where("toUserId", "==", userId))),
    getDocs(query(collection(db, SHARED_PATTERNS_COLLECTION), where("fromUserId", "==", userId))),
    getDocs(query(collection(db, SHARED_PRACTICE_PLANS_COLLECTION), where("toUserId", "==", userId))),
    getDocs(query(collection(db, SHARED_PRACTICE_PLANS_COLLECTION), where("fromUserId", "==", userId))),
    getDocs(query(collection(db, SHARED_PRACTICE_SESSIONS_COLLECTION), where("toUserId", "==", userId))),
    getDocs(query(collection(db, SHARED_PRACTICE_SESSIONS_COLLECTION), where("fromUserId", "==", userId))),
    getDocs(query(collection(db, SHARED_RECORDINGS_COLLECTION), where("toUserId", "==", userId))),
    getDocs(query(collection(db, SHARED_RECORDINGS_COLLECTION), where("fromUserId", "==", userId)))
  ]);
  
  // Combine all shared item IDs
  const sharedItemIds = [
    ...patternsWith.docs.map(doc => doc.id),
    ...patternsBy.docs.map(doc => doc.id),
    ...plansWith.docs.map(doc => doc.id),
    ...plansBy.docs.map(doc => doc.id),
    ...sessionsWith.docs.map(doc => doc.id),
    ...sessionsBy.docs.map(doc => doc.id),
    ...recordingsWith.docs.map(doc => doc.id),
    ...recordingsBy.docs.map(doc => doc.id)
  ];
  
  // If no shared items, return 0
  if (sharedItemIds.length === 0) {
    callback({ count: 0 });
    return () => {};
  }
  
  // Query for unread comments where user is not the sender
  const commentsRef = collection(db, SHARE_COMMENTS_COLLECTION);
  const q = query(
    commentsRef,
    where("sharedItemId", "in", sharedItemIds),
    where("senderId", "!=", userId),
    where("isRead", "==", false)
  );
  
  return onSnapshot(q, (snapshot) => {
    callback({ count: snapshot.docs.length });
  });
}

/**
 * Delete a shared item
 * @param {string} itemId - The shared item document ID
 * @param {string} collectionName - The collection name
 * @returns {Promise} - Promise that resolves when the delete is complete
 */
export async function deleteSharedItem(itemId, collectionName) {
  const itemRef = doc(db, collectionName, itemId);
  
  // Also delete any comments associated with this item
  const commentsRef = collection(db, SHARE_COMMENTS_COLLECTION);
  const commentsQuery = query(commentsRef, where("sharedItemId", "==", itemId));
  const commentsSnapshot = await getDocs(commentsQuery);
  
  // Delete all comments first
  const commentDeletePromises = commentsSnapshot.docs.map(doc => 
    deleteDoc(doc.ref)
  );
  
  await Promise.all(commentDeletePromises);
  
  // Then delete the main item
  return deleteDoc(itemRef);
}

// Fetch a shared pattern by ID
export async function getSharedPattern(patternId) {
  try {
    console.log("[PWA Debug] Fetching pattern with ID:", patternId);
    const docRef = doc(db, "sharedPatterns", patternId);
    console.log("[PWA Debug] DocRef created:", docRef.path);
    
    const docSnap = await getDoc(docRef);
    console.log("[PWA Debug] DocSnap exists:", docSnap.exists());
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log("[PWA Debug] Data retrieved successfully");
      
      // Check if this is a chunked pattern
      if (data.isChunked && data.patternData?.isChunkedPattern) {
        console.log("[PWA Debug] Loading chunked pattern with", data.chunkCount, "chunks");
        
        try {
          // Load chunks
          const chunksRef = collection(docRef, 'chunks');
          const chunksSnapshot = await getDocs(chunksRef);
          const chunks = [];
          
          chunksSnapshot.forEach(chunkDoc => {
            chunks.push(chunkDoc.data());
          });
          
          console.log(`[PWA Debug] Loaded ${chunks.length} chunks for pattern`);
          
          // Reconstruct the full pattern
          const reconstructedPatternData = reconstructPatternFromChunks(data.patternData, chunks);
          
          // Try to increment views only if allowed
          try {
            await updateDoc(docRef, {
              views: increment(1)
            });
          } catch (updateError) {
            console.log("[PWA Debug] View update failed, continuing:", updateError.message);
          }

          return {
            ...data,
            patternData: reconstructedPatternData,
            _wasChunked: true,
            _chunkCount: chunks.length
          };
        } catch (chunkError) {
          console.error("[PWA Debug] Error loading chunks:", chunkError);
          // Fallback: return main document without chunks
          return {
            ...data,
            _chunkLoadError: true
          };
        }
      } else {
        // Regular pattern - return as is
        console.log("[PWA Debug] Regular (non-chunked) pattern");
        
        // Try to increment views only if allowed
        try {
          await updateDoc(docRef, {
            views: increment(1)
          });
        } catch (updateError) {
          console.log("[PWA Debug] View update failed, continuing:", updateError.message);
        }

        return data;
      }
    } else {
      // Add a more detailed error for debugging
      console.error("[PWA Debug] Pattern not found. ID format:", 
                   typeof patternId, "Length:", patternId.length, 
                   "First 5 chars:", patternId.substring(0, 5));
      throw new Error("Pattern not found");
    }
  } catch (error) {
    console.error("[PWA Debug] Error in getSharedPattern:", error);
    throw error;
  }
}

// ===== Recording Sharing Functions =====

/**
 * Share an individual recording with a connected user.
 *
 * The recordingData should already include embedded annotations
 * (markers, comments, regions) and, for video recordings, the unified
 * fields (mediaType, hlsPath, posterPath, recordingDocPath). We also
 * add `toUserId` to the owner's mediaRecordings doc's viewerUids so
 * Storage rules allow the recipient to read the HLS + poster.
 */
export async function shareRecording(recordingData, fromUserId, toUserId, message = "") {
  const ref = collection(db, SHARED_RECORDINGS_COLLECTION);
  const created = await addDoc(ref, {
    recordingData,
    mediaType: recordingData?.mediaType || 'audio',
    fromUserId,
    toUserId,
    message,
    sharedAt: serverTimestamp(),
    viewed: false,
    status: "shared"
  });

  // Best-effort: grant the recipient read access on the underlying video
  // artifacts. If the recording predates the unified recorder it has no
  // mediaRecordings doc and this is a no-op.
  if (recordingData?.id && fromUserId && toUserId) {
    try {
      const recDocRef = doc(db, 'users', fromUserId, 'mediaRecordings', recordingData.id);
      await setDoc(recDocRef, { viewerUids: arrayUnion(toUserId) }, { merge: true });
    } catch (e) {
      console.warn('Failed to extend viewerUids for shared recording', recordingData.id, e?.message);
    }
  }

  return created;
}

export function getSharedRecordingsWithUser(userId, callback) {
  const ref = collection(db, SHARED_RECORDINGS_COLLECTION);
  const q = query(ref, where("toUserId", "==", userId), orderBy("sharedAt", "desc"));
  return onSnapshot(q, callback, (err) => {
    console.warn('sharedRecordings listener (withUser) error:', err.message);
    callback({ docs: [] });
  });
}

export function getSharedRecordingsByUser(userId, callback) {
  const ref = collection(db, SHARED_RECORDINGS_COLLECTION);
  const q = query(ref, where("fromUserId", "==", userId), orderBy("sharedAt", "desc"));
  return onSnapshot(q, callback, (err) => {
    console.warn('sharedRecordings listener (byUser) error:', err.message);
    callback({ docs: [] });
  });
}

export async function markSharedRecordingAsViewed(recordingId) {
  const ref = doc(db, SHARED_RECORDINGS_COLLECTION, recordingId);
  return updateDoc(ref, { viewed: true, viewedAt: serverTimestamp() });
}

// ===== Public Recording Sharing (link-shareable) =====

/**
 * Snapshot the public-facing fields of a recording into a new
 * publicSharedRecordings doc. The doc is the access token: anyone with
 * the resulting shareId can read it and play the underlying media.
 *
 * For HLS video, also flips `publicShare: true` on the source
 * mediaRecordings doc so the Storage rule starts serving the segments
 * publicly. Audio uses the legacy public-read Storage path so no
 * Storage rule change is needed.
 *
 * @param {Object} recording  - The recording entry (has id, mediaType,
 *                              audioUrl/hlsPath/posterPath, storagePath,
 *                              duration, etc.)
 * @param {string} ownerUid   - uid of the recording owner; must match
 *                              auth.currentUser.uid for the create rule.
 * @param {Object} extras     - Optional { title, sessionTitle } for nicer
 *                              display on the public share page.
 * @returns {Promise<{shareId: string}>}
 */
export async function shareRecordingPublicly(recording, ownerUid, extras = {}) {
  if (!recording?.id) throw new Error('shareRecordingPublicly: recording.id required');
  if (!ownerUid) throw new Error('shareRecordingPublicly: ownerUid required');

  const mediaType = recording.mediaType || 'audio';
  const payload = {
    recordingId: recording.id,
    ownerUid,
    mediaType,
    audioUrl: recording.audioUrl || null,
    hlsPath: recording.hlsPath || null,
    posterPath: recording.posterPath || null,
    storagePath: recording.storagePath || null,
    title: extras.title || recording.title || 'FlowFrame recording',
    sessionTitle: extras.sessionTitle || null,
    durationSec: typeof recording.duration === 'number' ? recording.duration : null,
    createdAt: serverTimestamp(),
    revoked: false,
    viewCount: 0,
    // Mirror onto the doc so the Storage rule can find it without an
    // extra lookup. (The Storage rule reads mediaRecordings, not this
    // collection, but having recordingDocPath here makes orphan-check
    // cleanup straightforward.)
    recordingDocPath: `users/${ownerUid}/mediaRecordings/${recording.id}`
  };

  const created = await addDoc(collection(db, PUBLIC_SHARED_RECORDINGS_COLLECTION), payload);

  // For video (HLS), open up Storage. Best-effort: legacy recordings
  // without a mediaRecordings doc don't need the flag (audio path is
  // already public-read); silently ignore the failure.
  if (mediaType === 'video') {
    try {
      const recDocRef = doc(db, 'users', ownerUid, 'mediaRecordings', recording.id);
      await setDoc(recDocRef, { publicShare: true }, { merge: true });
    } catch (e) {
      console.warn('shareRecordingPublicly: failed to set publicShare flag', recording.id, e?.message);
    }
  }

  return { shareId: created.id };
}

/**
 * Look up a public share by its shareId. Used by the /r/:shareId viewer.
 *
 * Increments viewCount best-effort (the rule allows anonymous viewCount
 * updates). Returns null if missing or revoked so the caller can render
 * a generic "no longer available" page without leaking that the share
 * ever existed.
 */
export async function getPublicShare(shareId) {
  if (!shareId) return null;
  const ref = doc(db, PUBLIC_SHARED_RECORDINGS_COLLECTION, shareId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  if (data.revoked) return null;

  // Best-effort view increment — failure is non-fatal.
  try {
    await updateDoc(ref, { viewCount: increment(1) });
  } catch (e) {
    // Anonymous-update rule allows only viewCount; if something else
    // landed here we'd hit this. Don't break the viewer over it.
    console.debug('viewCount increment failed:', e?.message);
  }

  return { shareId, ...data };
}

/**
 * Revoke an existing public share. Sets `revoked: true` (preserving the
 * doc for analytics/history) and, for video, clears the `publicShare`
 * flag so the Storage rule stops serving HLS publicly.
 */
export async function revokePublicShare(shareId, ownerUid) {
  if (!shareId) throw new Error('revokePublicShare: shareId required');
  const ref = doc(db, PUBLIC_SHARED_RECORDINGS_COLLECTION, shareId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  const data = snap.data();
  if (data.ownerUid !== ownerUid) {
    throw new Error('revokePublicShare: caller is not the share owner');
  }

  await updateDoc(ref, { revoked: true, revokedAt: serverTimestamp() });

  if (data.mediaType === 'video' && data.recordingId) {
    try {
      const recDocRef = doc(db, 'users', ownerUid, 'mediaRecordings', data.recordingId);
      await setDoc(recDocRef, { publicShare: false }, { merge: true });
    } catch (e) {
      console.warn('revokePublicShare: failed to clear publicShare flag', data.recordingId, e?.message);
    }
  }
  return true;
}

/**
 * Find the existing (non-revoked) public share for a given recording, if
 * any. The Share modal uses this to render the "Already shared · Copy
 * link · Revoke" state instead of creating a duplicate share each click.
 */
export async function getPublicShareForRecording(recordingId, ownerUid) {
  if (!recordingId || !ownerUid) return null;
  const ref = collection(db, PUBLIC_SHARED_RECORDINGS_COLLECTION);
  const q = query(
    ref,
    where('ownerUid', '==', ownerUid),
    where('recordingId', '==', recordingId),
    where('revoked', '==', false)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  // Return the most recent one (in case of historical duplicates).
  let newest = null;
  snap.forEach((d) => {
    const data = d.data();
    const ts = data.createdAt?.toMillis ? data.createdAt.toMillis() : 0;
    if (!newest || ts > newest._ts) newest = { shareId: d.id, _ts: ts, ...data };
  });
  if (newest) delete newest._ts;
  return newest;
}

// Create a new shared pattern and return its ID
export async function createSharedPattern(patternData) {
  try {
    console.log("Creating shared pattern with data:", patternData);
    const sharedPatternsRef = collection(db, "sharedPatterns");
    
    // Create the document data
    const docData = {
      patternData: patternData, // Store the entire pattern data object
      creatorId: auth.currentUser?.uid || null,
      createdAt: serverTimestamp(),
      views: 0
    };
    
    console.log("Saving document with data:", docData);
    const docRef = await addDoc(sharedPatternsRef, docData);
    return docRef.id;
  } catch (error) {
    console.error("Error creating shared pattern:", error);
    throw error;
  }
} 